import React, { useEffect, useRef } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { SignInPage } from '@/pages';
import RepoDetailPage from '@/pages/repossessions/RepoDetailPage';
import { Layout } from '@/components';
import { routes, DASHBOARD_ROUTE, HOME_ROUTE, getRouteMeta } from '@/router';
import useFeatureFlag from '@/hooks/useFeatureFlag';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 font-sans text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  return (
    <Layout
      currentPath={location.pathname}
      routeMeta={getRouteMeta(location.pathname)}
    >
      {children}
    </Layout>
  );
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          <p className="mt-4 font-sans text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/oems" replace />;
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  const showDashboard = useFeatureFlag('web_fms_show_dashboard', 'boolean');
  const showHome = useFeatureFlag('web_fms_show_home', 'boolean');

  const allRoutes = [
    ...(showDashboard ? [DASHBOARD_ROUTE] : []),
    ...(showHome ? [HOME_ROUTE] : []),
    ...routes,
  ];

  return (
    <Routes>
      <Route
        path="/signin"
        element={
          <PublicRoute>
            <SignInPage />
          </PublicRoute>
        }
      />
      {allRoutes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={<ProtectedRoute>{route.element}</ProtectedRoute>}
        />
      ))}
      {/* Dynamic repo detail route */}
      <Route
        path="/repossessions/:slug"
        element={
          <ProtectedRoute>
            <RepoDetailPage />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const supportsPopover = (): boolean =>
  typeof HTMLElement !== 'undefined' &&
  typeof HTMLElement.prototype.showPopover === 'function';

/**
 * Toasts live in a manual popover so they paint in the top layer above open
 * `<dialog>` sheets (RESPONSIVE_WEB_PLAN, OV1). Without the Popover API the
 * container renders in the normal flow as before.
 */
const ToastHost: React.FC = () => {
  const hostRef = useRef<HTMLDivElement>(null);
  const usePopover = supportsPopover();

  useEffect(() => {
    const host = hostRef.current;
    if (!usePopover || !host) return undefined;

    let frame = 0;
    const show = () => {
      try {
        if (host.isConnected && !host.matches(':popover-open')) {
          host.showPopover();
        }
      } catch {
        // Already shown; the container is still rendered.
      }
    };
    // The top layer paints in insertion order, so a <dialog> opened after
    // this host covers it. Re-entering puts the host back on top, but
    // `hidePopover()` only *requests* removal (processed at the next
    // rendering update) and a `showPopover()` in the same task would keep
    // the old slot, so the show waits two frames.
    const raise = () => {
      if (!document.querySelector('dialog[open]')) return;
      try {
        if (host.matches(':popover-open')) host.hidePopover();
      } catch {
        return;
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(show);
      });
    };

    show();
    const unsubscribe = toast.onChange((item) => {
      if (item.status === 'added') raise();
    });
    return () => {
      unsubscribe();
      cancelAnimationFrame(frame);
      try {
        if (host.matches(':popover-open')) host.hidePopover();
      } catch {
        // Ignore: unmounting anyway.
      }
    };
  }, [usePopover]);

  const container = (
    <ToastContainer
      position="bottom-right"
      autoClose={5000}
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="colored"
    />
  );

  if (!usePopover) return container;

  return (
    // Popover UA styles (inset, margin, border, background, size) are reset so
    // the host is an invisible full-viewport layer; react-toastify positions
    // its own fixed containers inside it.
    <div
      id="toast-host"
      ref={hostRef}
      popover="manual"
      className="fixed inset-0 m-0 p-0 h-auto w-auto max-h-none max-w-none border-0 bg-transparent overflow-visible pointer-events-none [&>*]:pointer-events-auto"
    >
      {container}
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <div className="App">
          <AppContent />
          <ToastHost />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
